import {
	ActionIcon,
	Checkbox,
	Divider,
	Group,
	rem,
	ScrollArea,
	Stack,
	Text,
	UnstyledButton,
} from '@mantine/core';
import { Fragment, memo, useMemo } from 'react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import {
	useTranslationCapChart,
	useTranslationCapTable,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { groupChartSecurities } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/capChartSecurities.utils.ts';
import { ChartSecuritiesTct } from '@dealstack/shared/lib/generated';

interface Props {
	securities: ChartSecuritiesTct[];
	selectedSecurities: Map<string, boolean>;
	selectedCount: number;
	totalCount: number;
	onSecuritiesSelected: (securityIds: string[], value: boolean) => void;
	onSelectAll: () => void;
	onBack: () => void;
}

/** The Specify securities view of the chart settings popover; selection applies immediately. */
const CapChartSecuritiesPanel = ({
	securities,
	selectedSecurities,
	selectedCount,
	totalCount,
	onSecuritiesSelected,
	onSelectAll,
	onBack,
}: Props) => {
	const tCapTable = useTranslationCapTable();
	const tCapChart = useTranslationCapChart();
	const tCommonUi = useTranslationCommonUi();

	const groups = useMemo(() => groupChartSecurities(securities), [securities]);
	// Issuer names only disambiguate when more than one entity issues securities.
	const showIssuers = groups.length > 1;

	const formatVotesPerUnit = (votesPerUnit: number | null) =>
		votesPerUnit
			? tCapChart('specify_securities.votes_per_unit', { count: votesPerUnit })
			: tCapChart('specify_securities.non_voting');

	return (
		<>
			<Group gap={8} wrap="nowrap" pl={8} pr={12} py={8}>
				<ActionIcon
					variant="subtle"
					color="dark.8"
					size={32}
					radius={4}
					onClick={onBack}
					aria-label={tCommonUi('back')}
				>
					<Icon variant="arrow-left-v3" size="lg" />
				</ActionIcon>
				<Text size="B6Bold" c="dark.8" flex={1} miw={0} truncate>
					{tCapTable('header_actions.specify_securities')}
				</Text>
				<UnstyledButton h={36} onClick={onSelectAll}>
					<Text size="B6Bold" c="blue.6">
						{tCommonUi('common_buttons.select_all')}
					</Text>
				</UnstyledButton>
			</Group>
			<Divider color="gray.2" />

			<ScrollArea.Autosize mah={rem(320)}>
				<Stack gap={0} pt={4} pb={8} px={8}>
					{groups.map(({ key, issuerName, securities: groupSecurities }) => (
						<Fragment key={key}>
							{showIssuers && issuerName && (
								<Text fz="sm" fw={600} lh={rem(16)} c="gray.9" px={8} pt={8} pb={4}>
									{issuerName}
								</Text>
							)}
							{groupSecurities.map((security) => {
								const checked = !!selectedSecurities.get(security.id);
								const isLastSelected = checked && selectedCount === 1;
								return (
									<Checkbox.Card
										key={security.id}
										checked={checked}
										disabled={isLastSelected}
										onChange={(value) =>
											onSecuritiesSelected([security.id], value)
										}
										withBorder={false}
										radius={4}
										p={8}
									>
										<Group gap={8} wrap="nowrap">
											<Checkbox.Indicator
												size="xs"
												color="blue.6"
												disabled={isLastSelected}
											/>
											<Text
												size="B6Medium"
												c="dark.8"
												flex={1}
												miw={0}
												truncate
											>
												{security.name}
											</Text>
											<Text
												fz="sm"
												lh={rem(16)}
												c="gray.9"
												style={{ whiteSpace: 'nowrap' }}
											>
												{formatVotesPerUnit(security.votesPerUnit)}
											</Text>
										</Group>
									</Checkbox.Card>
								);
							})}
						</Fragment>
					))}
				</Stack>
			</ScrollArea.Autosize>

			<Divider color="gray.2" />
			<Text fz="sm" lh={rem(16)} c="gray.9" px={16} pt={8} pb={12}>
				{tCapChart('specify_securities.selected_count', {
					selected: selectedCount,
					total: totalCount,
				})}
			</Text>
		</>
	);
};

const Memoized = memo(CapChartSecuritiesPanel);

export { Memoized as CapChartSecuritiesPanel };
